import { useComponent } from "@/hooks/useMicrositeComponent";
import { IMicrositeComponent } from "@/types/microsite";
import { youtubeMusicRule, youtubeRule } from "@/vars/media-rule";
import { PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

const AutoPlayItem = ({ code, noAutoPlay = false }) => {
  const [isReady, setIsReady] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [endTime, setEndTime] = useState(0);
  const [title, setTitle] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlay, setIsPlay] = useState(false);
  const intervalRef = useRef<any>(undefined);
  const [youtubeURL, setYoutubeURL] = useState("");

  useEffect(() => {
    const embed = document.createElement("script");
    embed.setAttribute("async", "");
    embed.setAttribute("src", "https://www.youtube.com/iframe_api");
    document.body.appendChild(embed);

    let playerState = 0;
    const w = window as any;

    const tryAutoplay = () => {
      try {
        if (process.env.NODE_ENV !== "production") console.log("tryAutoplay");
        w.PL.playVideo();
        setTimeout(() => {
          const state = w.PL.getPlayerState();
          if (state === 1) {
            setIsTouched(true);
            removeInteractionListeners();
          } else {
            if (process.env.NODE_ENV !== "production")
              console.log("still not playing");
          }
        }, 500);
      } catch (err) {
        if (process.env.NODE_ENV !== "production")
          console.log("Autoplay failed, waiting for user interaction");
      }
    };

    const interactionEvents = ["click", "scroll", "keydown"];
    const addInteractionListeners = () => {
      interactionEvents.forEach((event) =>
        window.addEventListener(event, tryAutoplay, { once: true })
      );
    };
    const removeInteractionListeners = () => {
      interactionEvents.forEach((event) =>
        window.removeEventListener(event, tryAutoplay)
      );
    };

    const initPlayer = () => {
      w.PL = new w.YT.Player("auto-play-container", {
        height: "1",
        width: "1",
        videoId: code,
        playerVars: {
          playsinline: 1,
          controls: 0,
          loop: 1,
        },
        events: {
          onReady: () => {
            setIsReady(true);
            setTitle(w.PL.getIframe().title);
            setEndTime(w.PL.getDuration());
            setYoutubeURL(w.PL.getVideoUrl());

            if (process.env.NODE_ENV !== "production") console.log("on ready");
            // Attempt autoplay
            if (!noAutoPlay) {
              w.PL.playVideo();
              setTimeout(() => {
                const state = w.PL.getPlayerState();
                if (state !== 1) {
                  if (process.env.NODE_ENV !== "production")
                    console.log("is not playing");
                  addInteractionListeners(); // fallback if autoplay blocked
                }
              }, 500);
            }
          },
          onStateChange: (event) => {
            if (event.data === 1) {
              setIsPlay(true);
              playerState = 1;
            } else {
              setIsPlay(false);
              playerState = 0;
            }
          },
        },
      });

      intervalRef.current = setInterval(() => {
        if (playerState === 1) {
          setCurrentTime(w.PL.getCurrentTime());
        }
      }, 1000);
    };

    w.onYouTubeIframeAPIReady = () => {
      initPlayer();
    };

    if (typeof w.YT !== "undefined" && w.YT.loaded) {
      initPlayer();
    }

    return () => {
      (window as any).PL?.destroy?.();
      clearInterval(intervalRef.current);
      removeInteractionListeners();
    };
  }, [code]);

  const togglePlay = () => {
    const w = window as any;
    if (isPlay) {
      w.PL.pauseVideo();
    } else {
      w.PL.playVideo();
    }
  };

  return (
    <div className="p-2 w-full">
      <div id="auto-play-container" className="opacity-0" />
      <div className="relative bg-white rounded-md text-black">
        <div
          className="relative flex items-center bg-white p-3 rounded-md overflow-hidden cursor-pointer"
          onClick={togglePlay}
        >
          <div className="top-0 right-0 bottom-0 left-0 absolute bg-white w-full overflow-hidden">
            <div
              style={{ width: `${(currentTime / endTime) * 100}%` }}
              className="bg-slate-100 h-full transition-all"
            />
          </div>

          <div
            onClick={togglePlay}
            className="z-10 relative bg-slate-200 p-2 rounded-full w-8 h-8 shrink-0"
          >
            {isPlay ? <PauseIcon /> : <PlayIcon />}
          </div>

          <p className="z-10 relative pl-3 font-semibold text-sm truncate">
            {title}
          </p>
        </div>
        <div className="flex px-2 py-1 leading-none" style={{ fontSize: 10 }}>
          <p className="flex-1">Auto Play</p>
          <a href={youtubeURL} className="underline">
            Watch on Youtube
          </a>
        </div>
      </div>
      {/* {isReady && !isTouched && !noAutoPlay ? (
        <div
          className="top-0 right-0 bottom-0 left-0 fixed bg-transparent autoplay-event"
          onClick={onClickAutoPlay}
          onTouchStart={onClickAutoPlay}
        />
      ) : (
        <Fragment />
      )} */}
    </div>
  );
};

const AutoPlayMusic = {
  kind: "AUTOPLAY_MUSIC",
  Render: ({ data, isBuilder, id }) => {
    const { focusTo } = useComponent();

    const { isValidMedia, code } = useMemo(() => {
      let isValidMedia = false;
      let code = "";

      if (youtubeRule.test(data.url)) {
        isValidMedia = true;
        const match = data.url.match(youtubeRule);
        code = match ? match[2] || match[3] || match[4] : "";
      } else if (youtubeMusicRule.test(data.url)) {
        isValidMedia = true;
        const match = data.url.match(youtubeMusicRule);
        code = match ? match[1] : "";
      }

      return { isValidMedia, code };
    }, [data.url]);

    if (!isValidMedia) return <Fragment />;

    return (
      <div
        onClick={() => {
          if (isBuilder) {
            focusTo(id || "");
          }
        }}
        className="relative w-full"
        id={`mcomp-${id}`}
      >
        {isBuilder && (
          <div className="top-0 right-0 bottom-0 left-0 z-20 absolute opacity-0" />
        )}
        <AutoPlayItem code={code} key={code} noAutoPlay={isBuilder} />
      </div>
    );
  },
} as IMicrositeComponent<any>;

export default AutoPlayMusic;
